import { env } from '@/lib/env';

// Camada de comunicação única (SDK interno). Substitui o api-client.js legacy.
// Prefixo canónico /api/v1. Token de sessão injetado por getToken (Clerk).
const BASE = `${env.apiOrigin}/api/v1`;

type TokenGetter = () => Promise<string | null>;
let tokenGetter: TokenGetter = async () => null;

/** Ligado uma vez pelo AuthProvider (Clerk). */
export function setTokenGetter(fn: TokenGetter) {
  tokenGetter = fn;
}

/** Base canónica da API (/api/v1). Para quem precisa de fetch cru (SSE streaming). */
export const API_BASE = BASE;

/** Token de sessão Clerk atual — a MESMA fonte que o http usa. Para fetch cru
 *  (streams SSE) que não passa pelo request() acima. Nunca ler de localStorage:
 *  o legado que guardava JWT no localStorage foi extinto no cutover. */
export function getAuthToken(): Promise<string | null> {
  return tokenGetter();
}

// A impersonação auditada (/access/impersonation) saiu da app de produto: é uma
// ferramenta da equipa e pertence à consola HQ. Aqui só viaja o token de sessão.
function identityHeaders(token: string | null): Record<string, string> {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Rotas PESSOAIS: sem sessão a API só pode responder 401. Em vez de gastar a
// viagem (e sujar a consola/monitorização com 401s «normais» de cada convidado),
// falha-se localmente com o mesmo ApiError. Quando o Clerk assenta como
// autenticado, o TokenBridge invalida as queries e elas repetem já com token.
const PERSONAL = /^\/(users\/me|access\/me|notifications|subscriptions\/me|wallet\/me)(\/|\?|$)/;

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await tokenGetter();
  if (!token && PERSONAL.test(path)) throw new ApiError(401, `Sem sessão para ${path}`);
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...identityHeaders(token),
      ...init.headers,
    },
  });

  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = await res.text().catch(() => undefined);
    }
    throw new ApiError(res.status, `API ${res.status} em ${path}`, body);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Upload multipart (avatar, media). NÃO define Content-Type — o browser
 *  gera o boundary. Reutiliza o mesmo token de sessão do request JSON. */
async function upload<T>(path: string, file: File, field = 'file'): Promise<T> {
  const token = await tokenGetter();
  const fd = new FormData();
  fd.append(field, file);
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: identityHeaders(token),
    body: fd,
  });
  if (!res.ok) {
    let body: unknown;
    try { body = await res.json(); } catch { body = await res.text().catch(() => undefined); }
    throw new ApiError(res.status, `API ${res.status} em ${path}`, body);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const http = {
  get: <T>(path: string) => request<T>(path),
  upload,
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
